import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useSnackbar } from "notistack";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, ListItemButton, ListItemIcon, List, ListItem, ListItemText, Checkbox, CircularProgress, TextField, Autocomplete, Typography, Grid } from "@mui/material";
import { sendClaimApprovalApi, sendShopVisitApprovalApi, sendWorkscopeApprovalApi, } from "../apiServices";
import { useGetApiHook } from "../hooks/useApiHook";
import { globalPutService } from "../../../utils/globalApiServices";
import { checkApiStatus, getGlobalCookie } from "../../../utils";
import { PageLoader } from "../../../shared_elements";
import { trackActivity } from "../../../utils/mixpanel";

const AssignUserModal = ({params, modalData, userData, onSearchUserField, toggleModalFn, getResponseBack, pageTitle,}) => {
  const userInfo = JSON.parse(getGlobalCookie("lessorAccess"));
  const { slug,leaseSlug } = useParams();
  const statusValue = userData?.status?.value;
  const [initialUsers, setInitialUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [remarks, setRemarks] = useState("");
  const [isLoading, setLoading] = useState({ pageLoader: false });
  const toggleLoader = (type, status) => setLoading({ [type]: status });
  const { enqueueSnackbar } = useSnackbar();
  const { getUserList } = useGetApiHook();
  useEffect(() => {
    if (!modalData?.assignUserModal) return;
    const fetchData = async () => {
      const userResponse = await getUserList(toggleLoader);
      const userToMove = userResponse?.find( (user) => user.id === userInfo?.user?.id );
      const otherUsers = userResponse?.filter( (user) => user.id !== userInfo?.user?.id );
      const finalData = userToMove ? [userToMove, ...otherUsers] : otherUsers;
      setInitialUsers(finalData);
    };
    fetchData();
  }, [modalData?.assignUserModal]);

  const filteredUsers = useMemo(() => {
    if (!modalData?.searchUser) return initialUsers;
    return initialUsers?.filter((user) => {
      const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`;
      const search = modalData?.searchUser?.toLowerCase();
      return (
        fullName.toLowerCase().includes(search) ||
        user?.email?.toLowerCase().includes(search)
      );
    });
  }, [modalData?.searchUser, initialUsers]);

  const handleCheckboxChange = (userId, isChecked) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [userId]: isChecked,
    }));
  };
  const selectedUserObjects = initialUsers?.filter((user) => selectedUsers[user.id]) || [];
  const handleMultiSelectChange = (event, newValue) => {
    const newSelectedIds = newValue.map((user) => user.id);
    const updatedSelected = {};
    newSelectedIds.forEach((id) => { updatedSelected[id] = true; });
    setSelectedUsers(updatedSelected);
  };
  const selectedUserIds =selectedUsers &&Object.keys(selectedUsers)?.filter((userId) => selectedUsers[Number(userId)])?.map(Number);
  const previousUserIds =userData?.approver_users?.filter((userId) => Number(userId?.id ? userId?.id : userId))?.map((ele) => ele.id) || [];
  const assignUserFn = () => {
    let updatedPayload;
    if (pageTitle === "Claim" || statusValue === 5) {updatedPayload = selectedUserIds;
    } else {updatedPayload = [...selectedUserIds, ...previousUserIds];
    }
    const uniqueIds = [...new Set(updatedPayload)];
    toggleLoader("buttonLoader", true);
    globalPutService(
      pageTitle === "ShopVisit"
        ? statusValue === 1 || statusValue === 2
          ? sendWorkscopeApprovalApi()
          : sendShopVisitApprovalApi()
        : sendClaimApprovalApi(params),
      pageTitle === "Claim"
        ? { id: userData.id, approver_users: uniqueIds, approver_remarks: remarks, lease_slug: leaseSlug }
        : { id: userData.id, approver_users: uniqueIds, lease_slug: leaseSlug }
    ).then((response) => {
      toggleLoader("buttonLoader", false);
      if (checkApiStatus(response)) {
        toggleModalFn();
        getResponseBack();
        setSelectedUsers({});
        setRemarks("");
        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" }, });
        trackActivity("Item Edited", {
          page_title: `Select Shop Visit Users`,
          success_msg: response?.data?.message,
          item_slug: slug,
          payload: pageTitle === "Claim" ? { approver_users: uniqueIds, approver_remarks: remarks } : uniqueIds,
          response: response?.data,
        });
      } else {
        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, });
        trackActivity("Item Edited", {
          page_title: `Select Shop Visit Users`,
          error_msg: response?.data?.message,
          item_slug: slug,
          payload: pageTitle === "Claim" ? { approver_users: uniqueIds, approver_remarks: remarks } : uniqueIds,
          response: response?.data,
        });
      }
    });
  };

  const handleItemClicked = (page_type) => {
    trackActivity("Item Clicked", {
      page_title: `Select Shop Visit Users`,
      page_type: page_type,
    });
  };
  return (
    <>
      {isLoading?.pageLoader ? (
        <PageLoader />
      ) : (
        <Dialog
          open={modalData?.assignUserModal}
          className="add-lease-dialog"
          maxWidth={pageTitle === "Claim" ?"sm" : "xs"}
          fullWidth
        >
          <DialogTitle className="dialog-header">User(s)</DialogTitle>
          <DialogContent
            dividers
            sx={pageTitle === "Claim" ? { p: 2 } : { p: 2, height: "450px", display: "flex", flexDirection: "column", }}
          >
            {pageTitle === "Claim" ? (
              <Grid container spacing={2}>
                <Grid size={{ md: 12, xs: 12 }}>
                  <Autocomplete
                    multiple
                    options={initialUsers || []}
                    value={selectedUserObjects}
                    onChange={handleMultiSelectChange}
                    getOptionLabel={(user) => `${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={isLoading?.buttonLoader}
                    renderOption={(props, user) => (
                      <Box component="li" {...props} key={user.id} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important" }}>
                        <Typography variant="body2">{`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}</Typography>
                        <Typography variant="caption" color="text.secondary">{user?.email || ""}</Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add User(s)"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ md: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    label="Remarks"
                    multiline
                    rows={3}
                    value={remarks}
                    onChange={(event) => setRemarks(event.target.value)}
                    disabled={isLoading?.buttonLoader}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              </Grid>
            ) : (
              <>
                <Box sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", pb: 1, }}>
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    value={modalData?.searchUser}
                    slotProps={{ htmlInput: { maxLength: 255 } }}
                    onChange={onSearchUserField}
                    variant="outlined"
                    placeholder="Search by name.."
                  />
                </Box>
                <Box
                  sx={{ overflow: "auto", border: "1px solid #c3c3c3", borderRadius: "4px", }}
                >
                  <List sx={{ p: 0 }}>
                    {filteredUsers?.length > 0 ? (
                      filteredUsers.map((user, index) => (
                        <ListItem
                          key={user.id}
                          sx={{
                            borderBottom:
                              index < filteredUsers.length - 1
                                ? "1px solid #c3c3c3"
                                : "none",
                          }}
                          disablePadding
                        >
                          <ListItemButton dense sx={{ flexWrap: "wrap" }}>
                            <ListItemIcon>
                              <Checkbox
                                color="primary"
                                checked={selectedUsers[user.id] || false}
                                onChange={(event) =>
                                  handleCheckboxChange(
                                    user.id,
                                    event.target.checked
                                  )
                                }
                                disabled={
                                  user?.isDisabled || isLoading?.buttonLoader
                                }
                              />
                            </ListItemIcon>

                            <ListItemText
                              primary={`${user?.first_name || ""} ${
                                user?.last_name || ""
                              }`}
                              secondary={user?.email || ""}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    ) : (
                      <Box className="no-record-found">No Records Found</Box>
                    )}
                  </List>
                </Box>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              className="cancel-btn"
              onClick={() => {
                toggleModalFn();
                handleItemClicked("Cancel Button");
              }}
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>

            <Button
              className="btn-primary"
              disabled={ isLoading?.buttonLoader ? true : selectedUserIds?.length ? false : true }
              variant="contained"
              onClick={() => {
                assignUserFn();
                handleItemClicked("Save Button");
              }}
              size="small"
            >
              {isLoading?.buttonLoader ? (
                <CircularProgress size={24} />
              ) : (
                "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AssignUserModal;
